/**
 * Hierarchy selection helper functions for filtering and expanding selections.
 */

export interface SelectionRow extends Record<string, unknown> {
  id: string
  parent_id: string | null
}

/**
 * Filters selected rows to keep only the topmost parents.
 * Removes rows whose parent_id chain (among the selected set) includes another selected id.
 */
export function filterSelectionRoots<T extends SelectionRow>(rows: T[]): T[] {
  const selectedIds = new Set(rows.map(r => r.id))
  
  return rows.filter(row => {
    // Walk up the parent chain to see if any parent is also selected
    let parentId = row.parent_id
    while (parentId != null) {
      if (selectedIds.has(parentId)) {
        return false // This row has a selected ancestor, so filter it out
      }
      // Find the parent row to continue walking up
      const parentRow = rows.find(r => r.id === parentId)
      if (!parentRow) break // Parent not found, stop walking
      parentId = parentRow.parent_id
    }
    return true // No selected ancestor found, keep this row
  })
}

/**
 * Expands a selection with loaded descendants using the loadedDescendantIds function.
 * Uses the loadedDescendantIds function to get descendants for each selected id.
 */
export function expandLoadedSelection(
  selectedIds: Set<string>,
  loadedDescendantIds: (rootId: string) => string[]
): Set<string> {
  const result = new Set(selectedIds)
  
  for (const selectedId of Array.from(selectedIds)) {
    const descendants = loadedDescendantIds(selectedId)
    for (const descendantId of descendants) {
      result.add(descendantId)
    }
  }
  
  return result
}